// web/src/admin/login.tsx
import { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import useAdmin from './hooks/useAdmin';
import { BASE_URL } from '../shared/api';
import ErrorDisplay from '../shared/components/ErrorDisplay';

interface FormData { login_id: string; password: string; }

export default function AdminLogin() {
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setToken, setError, error, refreshCsrf } = useAdmin();
  const navigate = useNavigate();

  useEffect(() => { refreshCsrf().catch(() => {}); }, [refreshCsrf]);

  const onSubmit = async (data: FormData) => {
    setLoading(true); setError('');
    try {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminInfo');
      const csrfToken = await refreshCsrf();
      const res = await fetch(`${BASE_URL}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken || '' },
        credentials: 'include',
        body: JSON.stringify({ login_id: data.login_id.trim(), password: data.password, _csrf: csrfToken }),
      });
      const result = await res.json();
      if (!res.ok) {
        if (res.status === 429) throw new Error('Too many attempts. Wait 15 minutes.');
        if (res.status === 401) throw new Error('Invalid Login ID or password.');
        throw new Error(result.error?.message || 'Login failed');
      }
      localStorage.setItem('adminToken', result.token);
      if (result.admin) localStorage.setItem('adminInfo', JSON.stringify(result.admin));
      setToken(result.token);
      navigate('/admin/dashboard');
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-800 to-pink-700 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="flex justify-center mb-4">
          <img src="/BolsaFish.png" alt="Bolsa Fish" className="bf-logo-ring h-20 w-20 rounded-full object-contain p-2" />
        </div>
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-1">Admin Login</h1>
        <p className="text-center text-gray-500 text-sm mb-6">Use your Admin Login ID to sign in</p>
        {error && <ErrorDisplay error={error} onDismiss={() => setError('')} />}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Admin Login ID</label>
            <input placeholder="e.g. SADMIN001 or ADM123456"
              {...register('login_id', { required: 'Login ID is required' })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 font-mono tracking-wider" />
            {errors.login_id && <p className="text-red-600 text-sm">{errors.login_id.message}</p>}
          </div>
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input type={showPassword ? 'text' : 'password'} placeholder="Enter your password"
              {...register('password', { required: 'Password required', minLength: { value: 6, message: 'Min 6 characters' } })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg pr-12 focus:ring-2 focus:ring-purple-600" />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-9 text-gray-600">
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
            {errors.password && <p className="text-red-600 text-sm">{errors.password.message}</p>}
          </div>
          <button type="submit" disabled={loading}
            className="bf-glossy-btn w-full py-3 text-white font-semibold rounded-lg transition disabled:opacity-70">
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        <p className="mt-3 text-center text-gray-500 text-sm">
          Manager login? <Link to="/manager/login" className="text-purple-600 hover:underline">Click here</Link>
        </p>
      </div>
    </div>
  );
}
